import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import InductionContext from '../../context/Induction/InductionContext';
import { CustomAlert } from '../CustomAlert/CustomAlert';
import { CustomPDFViewer } from '../CustomPDFViewer/CustomPDFViewer';
import { CustomPlayer } from '../CustomPlayer/CustomPlayer'
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { DownloadInduction } from '../DownloadInduction/DownloadInduction';
import { FullScreenPDFPreview } from '../FullScreenPDFPreview/FullScreenPDFPreview';

export const File = ({ induction, haveQuestionary, videoPercentage, setWasSeen }) => {

    const { previewInduction, /*previewInductionDeleteURL,*/ inductionState } = useContext(InductionContext);
    const { url, error, message } = inductionState;

    const [modal, setModal] = useState(false);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);

    // Estado para trackear páginas vistas de cada PDF
    // Estructura: { fileIndex: { seenPages: Set, totalPages: number } }
    const [pdfPagesSeen, setPdfPagesSeen] = useState(() => {
        const initialState = {};
        induction.files.forEach((file, index) => {
            if (file.type === 'application/pdf') {
                initialState[index] = { seenPages: new Set(), totalPages: 0 };
            }
        });
        return initialState;
    });
    const [videoSeen, setVideoSeen] = useState(false);

    const { t } = useTranslation();

    const currentFile = induction.files[currentFileIndex];

    // Callback para cuando se ve una página del PDF
    const handlePageChange = (seenPagesArray, totalPages) => {
        setPdfPagesSeen(prev => {
            const newState = { ...prev };
            if (!newState[currentFileIndex]) {
                newState[currentFileIndex] = { seenPages: new Set(), totalPages: 0 };
            }
            newState[currentFileIndex] = {
                seenPages: new Set(seenPagesArray),
                totalPages: totalPages
            };
            return newState;
        });
    };

    const optionsRender = {
        'application/pdf': (
            <div className="d-flex align-items-start gap-3">
                {
                    modal ?
                        <FullScreenPDFPreview
                            url={url}
                            modal={modal}
                            setModal={setModal}
                            viewer={<CustomPDFViewer fileUrl={url} onPageChange={handlePageChange} />}
                        />
                        :
                        null
                }


                {/* PDF Viewer */}
                <div className='flex-grow-1' style={{ height: '65vh' }}>
                    <CustomPDFViewer fileUrl={url} onPageChange={handlePageChange} />
                </div>

            </div>
        ),
        'video/mp4': (
            <div style={{ height: '60vh' }} >
                <CustomPlayer
                    url={url}
                    videoPercentage={videoPercentage}
                    setWasSeen={() => {
                        setVideoSeen(true);
                        checkAllFilesSeen();
                    }}
                />
                {
        induction.allow_download ?
            <div>
                <DownloadInduction inductionName={currentFile.name} />
            </div>
            :
            null
    }
            </div >
        )
    }

useEffect(() => {
    // Llamar a previewInduction cuando cambia el archivo actual
    if (currentFile && currentFile.name) {
        previewInduction(currentFile.name);
    }
    // Resetear el tracking de páginas cuando cambia el archivo (solo para PDFs)
    if (currentFile && currentFile.type === 'application/pdf') {
        setPdfPagesSeen(prev => {
            const newState = { ...prev };
            if (!newState[currentFileIndex]) {
                newState[currentFileIndex] = { seenPages: new Set(), totalPages: 0 };
            }
            return newState;
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [currentFileIndex]);

// Llamar a previewInduction cuando el componente se monta
useEffect(() => {
    if (currentFile && currentFile.name) {
        previewInduction(currentFile.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Función para verificar si todos los archivos fueron vistos
const checkAllFilesSeen = () => {
    const hasVideo = induction.files.some(file => file.type === 'video/mp4');

    // Verificar que todos los PDFs tengan todas sus páginas vistas
    const allPdfsSeen = induction.files.every((file, index) => {
        if (file.type !== 'application/pdf') {
            return true; // No es PDF, no requiere verificación
        }
        const pdfData = pdfPagesSeen[index];

        // Si el PDF aún no se ha cargado (no tiene totalPages)
        if (!pdfData || pdfData.totalPages === 0) {
            // Si es el archivo actual que se está cargando, no lo penalizamos
            // porque aún se está cargando. Solo verificamos PDFs que ya tienen totalPages.
            if (index === currentFileIndex) {
                // El PDF actual se está cargando, no lo consideramos en la verificación aún
                return true; // Temporalmente true para no deshabilitar el botón mientras carga
            }
            // Para otros archivos que no son el actual, si no hay datos, no fue visto
            return false;
        }

        // Verificar que todas las páginas hayan sido vistas
        // pdfData.seenPages es un Set, así que usamos .size
        const seenPagesSize = pdfData.seenPages instanceof Set
            ? pdfData.seenPages.size
            : (Array.isArray(pdfData.seenPages) ? pdfData.seenPages.length : 0);
        const allPagesSeen = seenPagesSize === pdfData.totalPages;
        return allPagesSeen;
    });

    const videoRequirementMet = !hasVideo || videoSeen;

    setWasSeen(allPdfsSeen && videoRequirementMet);
};

// Verificar cuando cambian las páginas vistas o el video
useEffect(() => {
    checkAllFilesSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [pdfPagesSeen, videoSeen, currentFileIndex]);


return (
    <>
        <div>
            <h4>{induction.name}</h4>
            <p>{induction.description ? induction.description : '-'}</p>
        </div>
        {error ? <CustomAlert color={'danger'} text={message} /> : null}

        {optionsRender[currentFile.type]}

        {induction.files.length > 1 && (
            <div className="d-flex justify-content-center gap-3 mt-3">
                <button
                    className="btn btn-outline-primary"
                    disabled={currentFileIndex === 0}
                    onClick={() => setCurrentFileIndex(currentFileIndex - 1)}
                >
                    <i className="fa-solid fa-chevron-left"></i> {t('common.previous')}
                </button>
                <span className="align-self-center">
                    {currentFileIndex + 1} / {induction.files.length}
                </span>
                <button
                    className="btn btn-outline-primary"
                    disabled={currentFileIndex === induction.files.length - 1}
                    onClick={() => setCurrentFileIndex(currentFileIndex + 1)}
                >
                    {t('common.next')} <i className="fa-solid fa-chevron-right"></i>
                </button>
            </div>
        )}
    </>
)
}
